/**
 * As linhas de arquivo de um bloco da tela "Contratos digitais" (E177.F4, GUS-253; HTML 6.8 por CCB):
 *
 * - **em envio ou com falha** — a linha do lote, com o progresso, "Tentar novamente" e "Remover";
 * - **já recebida** — o arquivo do lote idêntico a um que já está no caso (E216.F7);
 * - **recebida** — "Recebido · integridade registrada no recibo", com o selo das outras CCBs que a
 *   usam, os vínculos (`acoes`) e "Remover";
 * - **vinculada** — o arquivo enviado em outra CCB, com "Compartilhado · enviado na CCB x" e
 *   "Desvincular desta CCB".
 *
 * A recebida e a vinculada abrem o arquivo (`abrir`) antes de decidir o vínculo (E177.F4.C02).
 */
import type { ReactNode } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { LinhaDoEnvio } from '@/lib/useEnvioPorRecebimento';

const t = ptBR.abertura.contratosDigitais;

const LINK = 'text-[13px] text-accent-700 underline underline-offset-2 disabled:opacity-60';

function Linha({
  nome,
  estado,
  testId,
  progresso,
  selo,
  children,
}: {
  nome: string;
  estado: string;
  testId: string;
  progresso?: number | undefined;
  selo?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="flex flex-wrap items-start justify-between gap-3 border-b border-border py-[10px] last:border-b-0"
      data-testid={testId}
    >
      <div className="min-w-0">
        <b className="block break-all text-[14px] text-fg">{nome}</b>
        <small className="block text-[13px] text-fg-muted">{estado}</small>
        {progresso !== undefined ? (
          <div aria-hidden className="mt-1 h-1 w-40 bg-surface-2">
            <i className="block h-1 bg-accent" style={{ width: `${progresso}%` }} />
          </div>
        ) : null}
        {selo !== undefined ? (
          <span className="mt-1 inline-block border border-accent-200 bg-accent-bg px-2 py-px text-[12px] text-accent-700">
            {selo}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

export function LinhaEmEnvio({
  linha,
  onTentarDeNovo,
  onRemover,
}: {
  linha: LinhaDoEnvio;
  onTentarDeNovo: () => void;
  onRemover: () => void;
}): JSX.Element {
  const falhou = linha.estado === 'falhou';
  return (
    <Linha
      nome={linha.fileName}
      estado={falhou ? t.falhou : t.enviando.replace('{p}', String(linha.percent))}
      progresso={falhou ? undefined : linha.percent}
      testId={`ccb-linha-${linha.localId}`}
    >
      {falhou && linha.retentavel ? (
        <button type="button" className={LINK} onClick={onTentarDeNovo}>
          {t.tentarDeNovo}
        </button>
      ) : null}
      {falhou ? (
        <button type="button" className={LINK} onClick={onRemover}>
          {t.remover}
        </button>
      ) : null}
    </Linha>
  );
}

/** E216.F7 (GUS-317 item 9): o arquivo do lote com os bytes de um que já está no caso — não foi
 * duplicado, e a linha fica com o aviso em vez de sumir calada. */
export function LinhaJaRecebida({ linha }: { linha: LinhaDoEnvio }): JSX.Element {
  return (
    <Linha nome={linha.fileName} estado={t.jaRecebido} testId={`ccb-ja-recebido-${linha.localId}`}>
      {null}
    </Linha>
  );
}

export function LinhaRecebida({
  nome,
  documentoId,
  compartilhadaCom,
  removendo,
  onRemover,
  abrir,
  acoes,
}: {
  nome: string;
  documentoId: string;
  /** Os rótulos das outras CCBs que usam o arquivo. */
  compartilhadaCom: readonly string[];
  removendo: boolean;
  onRemover: () => void;
  /** E177.F4.C02: abrir o arquivo para conferir o conteúdo. */
  abrir?: ReactNode;
  /** E177.F4.T04: vincular a outras CCBs ou editar os vínculos. */
  acoes?: ReactNode;
}): JSX.Element {
  return (
    <Linha
      nome={nome}
      estado={t.recebido}
      selo={
        compartilhadaCom.length > 0
          ? t.compartilhadoCom.replace('{ccbs}', compartilhadaCom.join(', '))
          : undefined
      }
      testId={`ccb-recebido-${documentoId}`}
    >
      {abrir}
      {acoes}
      <button type="button" className={LINK} onClick={onRemover} disabled={removendo}>
        {t.remover}
      </button>
    </Linha>
  );
}

export function LinhaVinculada({
  nome,
  documentoId,
  enviadaNa,
  nomeNaOrigem = null,
  desvinculando,
  onDesvincular,
  abrir,
}: {
  nome: string;
  documentoId: string;
  /** O rótulo da CCB em que o arquivo foi enviado; nulo no arquivo sem CCB vinculado aqui. */
  enviadaNa: string | null;
  /** E177.F3.C03: o nome na origem da cópia que este bloco recebeu com outro nome. */
  nomeNaOrigem?: string | null;
  desvinculando: boolean;
  onDesvincular: () => void;
  abrir?: ReactNode;
}): JSX.Element {
  return (
    <Linha
      nome={nome}
      estado={t.recebido}
      selo={
        enviadaNa === null
          ? t.vinculadoAqui
          : (nomeNaOrigem === null
              ? t.compartilhadoDe
              : t.compartilhadoDeComNome.replace('{nome}', nomeNaOrigem)
            ).replace('{ccb}', enviadaNa)
      }
      testId={`ccb-vinculado-${documentoId}`}
    >
      {abrir}
      <button type="button" className={LINK} onClick={onDesvincular} disabled={desvinculando}>
        {t.desvincular}
      </button>
    </Linha>
  );
}

export { LINK as LINK_DA_CCB };
